import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import type { WorldView } from '../data/types';
import type { RunDayEvent } from '../data/runDay';

/** Season rules in plain words. Fees are read live from the chain; the schedule from /launch.json. */
export function RaceRules({
  view,
  schedule,
  shoe,
  anyApp,
  earlyHours,
  event,
  onClose,
}: {
  view: WorldView;
  schedule: string;
  shoe: number | null;
  anyApp?: number;
  earlyHours: number;
  /** RUN DAY (Halloween flag): the shoe is a rolling jackpot, races follow the event schedule, forks burn or pay stakers. */
  event?: RunDayEvent;
  onClose: () => void;
}) {
  const close = useRef(onClose);
  close.current = onClose;
  const panel = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.preventDefault(); close.current(); }
      if (e.key === 'Tab') {
        const items = Array.from(panel.current?.querySelectorAll<HTMLElement>('button, a[href], input, summary') ?? []).filter(el => el.checkVisibility());
        const first = items[0], last = items.at(-1);
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last?.focus(); }
        if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first?.focus(); }
      }
    };
    window.addEventListener('keydown', onKey);
    panel.current?.querySelector<HTMLElement>('button')?.focus();
    return () => { window.removeEventListener('keydown', onKey); previous?.focus(); };
  }, []);
  const fee = (view.market.hookFeeBps / 100).toFixed(view.market.hookFeeBps % 100 ? 1 : 0);
  const lp = (view.market.lpFeePips / 10_000).toFixed(1);
  return createPortal(
    <div className="modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal race-rules" role="dialog" aria-modal="true" aria-labelledby="race-rules-h" ref={panel} data-testid="race-rules">
        <div className="modal-head">
          <h2 id="race-rules-h">How races work</h2>
          <button type="button" className="icon-btn" aria-label="Close" onClick={onClose}>
            ✕
          </button>
        </div>
        {event ? <RunDayRules view={view} event={event} schedule={schedule} jackpot={shoe} /> : <ol>
          <li>
            <strong>A race every day at {schedule}.</strong> The live state and course come from the game contracts.
          </li>
          <li>
            <strong>👟 Golden Shoe{shoe !== null ? `: $${shoe}` : ''}. How to get a ticket:</strong>
            <ul className="race-steps">
              <li>
                <strong>When:</strong> the app reports whether the Shoe entry window is open. Earlier buys do not get a Shoe entry. The separate team draw has its own window below.
              </li>
              <li>
                <strong>How:</strong> buy any amount of RUNNER on runner.party. Even $1 is a ticket: it costs about {Math.round(view.market.hookFeeBps / 100)}¢ in fees and you keep the
                RUNNER. Bigger buys get more tickets, and several buys add up.
              </li>
              <li>
                <strong>Until:</strong> the transaction must confirm before the onchain cutoff, before the runner can reach the Shoe. The race bar shows the
                countdown and a ✓ once you’re in.
              </li>
              <li>
                <strong>Win:</strong> if the runner grabs the Shoe and there are eligible entries, a winner is drawn. If it misses, nobody wins and the next race
                has a new shoe.
              </li>
            </ul>
          </li>
          {anyApp ? (
            <li>
              <strong>🎟️ Any-App Prize: ${anyApp}. Buying early counts.</strong> Canonical-pool net buys on participating apps are assessed by the team from{' '}
              {earlyHours} hours before the race until it ends. The advertised draw is ${anyApp}. More net buying means better
              odds (counted up to $200); buying and then selling doesn’t count. The draw uses public drand randomness published after the race, so
              nobody can know it in advance. This app does not currently receive verified team-draw selection or payout records.
            </li>
          ) : null}
          <li>
            <strong>One runner, controlled by the market.</strong> During an active race, eligible canonical-pool trades combine in {view.timing.windowSeconds}-second windows: buys push right, sells push left. Other pools and protocol buybacks do not steer. Between races, trading does not move the runner.
            Steer it into the shoe and someone wins.
          </li>
          <li>
            <strong>Every trade feeds the game.</strong> Part of every game fee funds the races and buys back and burns RUNNER, between races too.
          </li>
        </ol>}
        <p className="small muted">
          Every trade pays a {fee}% game fee plus the pool's {lp}% fee. {event ? 'The jackpot above is onchain USDG from the prize reserve.' : 'The Shoe amount above is onchain USDG. Any advertised team bonus is separate; its funding and payment are not verified here.'}
        </p>
      </div>
    </div>,
    document.body,
  );
}

function RunDayRules({ view, event, schedule, jackpot }: { view: WorldView; event: RunDayEvent; schedule: string; jackpot: number | null }) {
  const forks = event.forks ?? 3;
  const cap = event.forkBurnCap ?? 50;
  const last = event.times[event.times.length - 1];
  return (
    <ol>
      <li>
        <strong>{event.name ?? 'RUN DAY'}: {event.times.length} races on {new Intl.DateTimeFormat(undefined, { weekday: 'long', month: 'long', day: 'numeric', timeZone: 'UTC' }).format(Date.parse(`${event.date}T12:00:00Z`))}, at {schedule}.</strong> Each runs about {event.raceMinutes ?? 13} minutes. The {last} UTC race is the Witching Hour: its Forge gates burn up to $200 each. Live state and the course come from the game contracts.
      </li>
      <li>
        <strong>👟 Golden Shoe jackpot{jackpot !== null ? `: $${jackpot.toLocaleString(undefined, { maximumFractionDigits: 2 })}` : ''}. How to get a ticket:</strong>
        <ul className="race-steps">
          <li>
            <strong>How:</strong> buy RUNNER on runner.party during a race’s ticket window. Even $1 is a ticket: it costs about {Math.round(view.market.hookFeeBps / 100)}¢ in fees and you keep the RUNNER. A bigger buy carries more weight, and several buys add up.
          </li>
          <li>
            <strong>When:</strong> the window opens when the race starts and closes at the onchain cutoff, before I can reach the shoe. Buys before or after it, or on other apps, don’t get a ticket. The race bar shows the countdown and a ✓ once you’re in.
          </li>
          <li>
            <strong>Win:</strong> if I grab the shoe and there are tickets, one is drawn for the whole jackpot. If I miss, it rolls over until someone catches it.
          </li>
        </ul>
      </li>
      <li>
        <strong>🦇 Haunted forks.</strong> Each race has {forks} forks. Left is a Forge gate: it buys back and burns up to ${cap} of RUNNER from the burn pot. Right is a water station: it pays stakers. Sell to steer me left (burn), buy to steer me right (pay stakers).
      </li>
      <li>
        <strong>One runner, controlled by the market.</strong> During an active race, eligible canonical-pool trades combine in {view.timing.windowSeconds}-second windows: buys push right, sells push left. Other pools and protocol buybacks do not steer. Between races, trading does not move me.
      </li>
      <li>
        <strong>💧 Stake to earn.</strong> Deposit RUNNER on the Rewards page and you share what the water stations pay out.
      </li>
    </ol>
  );
}
